import { useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreateLmsVehicleTrainingMatchMutation, useDeleteLmsVehicleTrainingMatchMutation, useGetLmsVehicleTrainingMatchListQuery, useUpdateLmsVehicleTrainingMatchMutation } from '../../../../api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import toastService from '../../../../utils/toastService';

const FIELD_MAP = {
    vehicleName: 'vehicleName',
    trainingName: 'trainingName',
    isActive: 'isActive'
};

const initialFilters = {
    vehicleName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    trainingName: { value: [], matchMode: FilterMatchMode.EQUALS },
    isActive: { value: null, matchMode: FilterMatchMode.EQUALS }
};

const useVehicleTrainingMatchData = () => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [filters, setFilters] = useState(initialFilters);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState(null);

    const queryArgs = useMemo(() => {
        const apiFilters = Object.entries(filters).flatMap(([field, filter]) => {
            const value = filter?.value;
            if (field === 'trainingName') return [];
            if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return [];
            if (Array.isArray(value)) {
                return value.map((filterValue) => ({ column: FIELD_MAP[field], operator: matchModeToOperator[filter.matchMode] ?? 5, value: String(filterValue) }));
            }
            return [{ column: FIELD_MAP[field], operator: matchModeToOperator[filter.matchMode] ?? 5, value: String(value) }];
        });
        return {
            vehicleCode: null,
            trainingCodes: filters.trainingName?.value ?? [],
            isActive: 1,
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: apiFilters,
            sorts: sortField && sortOrder
                ? [{ column: FIELD_MAP[sortField], direction: sortOrder === 1 ? 'asc' : 'desc' }]
                : [],
            includeFilterOptions: true,
            filterOptionsColumns: Object.values(FIELD_MAP)
        };
    }, [filters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const { data = { items: [], totalCount: 0 }, isFetching } = useGetLmsVehicleTrainingMatchListQuery(queryArgs, { refetchOnMountOrArgChange: true });
    const groupedItems = useMemo(() => {
        const groups = new Map();
        data.items.forEach((item) => {
            const group = groups.get(item.vehicleCode);
            const training = {
                code: item.trainingCode,
                name: item.trainingName
            };

            if (group) {
                group.ids.push(item.id);
                if (!group.trainings.some(({ code }) => code === training.code)) group.trainings.push(training);
                group.isActive = group.isActive && item.isActive;
                return;
            }

            groups.set(item.vehicleCode, {
                ...item,
                ids: [item.id],
                trainings: [training]
            });
        });

        return Array.from(groups.values()).map((group) => {
            const trainings = group.trainings.sort((a, b) => a.name.localeCompare(b.name));
            return {
                ...group,
                trainings,
                trainingCodes: trainings.map(({ code }) => code)
            };
        });
    }, [data.items]);
    const [createMatch, { isLoading: isCreating }] = useCreateLmsVehicleTrainingMatchMutation();
    const [updateMatch, { isLoading: isUpdating }] = useUpdateLmsVehicleTrainingMatchMutation();
    const [deleteMatch, { isLoading: isDeleting }] = useDeleteLmsVehicleTrainingMatchMutation();

    const formik = useFormik({
        initialValues: { vehicleCode: '', trainingCodes: [] },
        validationSchema: Yup.object({
            vehicleCode: Yup.string().required(th('vehicleTrainingMatch.vehicleRequired')),
            trainingCodes: Yup.array().of(Yup.string()).min(1, th('vehicleTrainingMatch.trainingRequired')).required(th('vehicleTrainingMatch.trainingRequired'))
        }),
        onSubmit: async ({ vehicleCode, trainingCodes }, helpers) => {
            try {
                if (selectedMatch?.id) {
                    await updateMatch({ id: selectedMatch.id, vehicleCode, trainingCodes }).unwrap();
                    toastService.success(th('vehicleTrainingMatch.updateSuccess'), th('common.success'));
                } else {
                    await createMatch({ vehicleCode, trainingCodes }).unwrap();
                    toastService.success(th('vehicleTrainingMatch.createSuccess'), th('common.success'));
                }
                helpers.resetForm();
                setSelectedMatch(null);
                setDialogVisible(false);
            } catch {
                // Global RTK Query hata yöneticisi backend mesajını gösterir.
            }
        }
    });

    const removeMatch = async (match) => {
        if (isDeleting) return;
        try {
            for (const id of match.ids) await deleteMatch(id).unwrap();
            toastService.success(th('vehicleTrainingMatch.deleteSuccess'), th('common.success'));
            setSelectedMatch(null);
            setDialogVisible(false);
            return true;
        } catch {
            // Global RTK Query hata yöneticisi backend mesajını gösterir.
        }
    };

    return {
        items: groupedItems,
        totalCount: data.totalCount - data.items.length + groupedItems.length,
        isFetching,
        isCreating,
        isUpdating,
        isDeleting,
        formik,
        filters,
        sortField,
        sortOrder,
        lazyParams,
        dialogVisible,
        selectedMatch,
        openDialog: () => {
            setSelectedMatch(null);
            formik.resetForm();
            setDialogVisible(true);
        },
        openEditDialog: (row) => {
            setSelectedMatch(row);
            formik.setValues({ vehicleCode: row.vehicleCode, trainingCodes: row.trainingCodes });
            setDialogVisible(true);
        },
        closeDialog: () => {
            formik.resetForm();
            setSelectedMatch(null);
            setDialogVisible(false);
        },
        removeMatch,
        onPage: (event) => setLazyParams({ first: event.first, rows: event.rows, page: event.page + 1 }),
        onFilter: (event) => {
            setFilters(event.filters);
            setLazyParams((current) => ({ ...current, first: 0, page: 1 }));
        },
        onSort: (event) => {
            setSortField(event.sortField);
            setSortOrder(event.sortOrder);
            setLazyParams((current) => ({ ...current, first: 0, page: 1 }));
        }
    };
};

export default useVehicleTrainingMatchData;
